import { useCallback, useLayoutEffect, type RefObject } from 'react';

/**
 * Places a floating panel against the field that owns it, and keeps it there.
 *
 * This exists because getting it wrong is invisible on a desktop browser and
 * ruinous on a phone. The bug it was written for: on iOS, the medicine
 * suggestions were drawn *on top of* the search box, so the doctor could not see
 * what they were typing. Three things had to line up to produce that, and all
 * three are worth knowing before editing this file.
 *
 * 1. The app's pages scroll in an inner container (`AppShell`'s `<main
 *    overflow-auto>`), so the document itself never scrolls on mobile. When the
 *    software keyboard opens, Safari therefore cannot scroll the focused input
 *    into view the usual way — it pans the *visual viewport* instead.
 * 2. A visual-viewport pan fires no `scroll` or `resize` event on `window` or on
 *    any element. It is reported only on `window.visualViewport`. A panel that
 *    listens to the former and not the latter simply never hears the keyboard.
 * 3. `getBoundingClientRect()` answers in layout-viewport coordinates, while
 *    Safari re-anchors `position: fixed` elements against the *visual* viewport
 *    once a keyboard pan settles. Mixing the two makes the panel drift from its
 *    anchor by exactly the pan distance — which is how it ends up over the input.
 *
 * So the panel is positioned `absolute` in *document* coordinates rather than
 * `fixed` in viewport coordinates. Document space is immune to all of the above:
 * visual-viewport pans, pinch zoom and keyboard insets move the anchor and the
 * panel together, because they move the whole document. `fixed` would need us to
 * guess at the browser's re-anchoring rules; `absolute` needs us to guess at
 * nothing.
 *
 * The *available space*, on the other hand, is measured against the visual
 * viewport — with a keyboard up, that rectangle is the only part of the page the
 * user can actually see, and a panel sized to the layout viewport would render
 * most of itself behind the keyboard.
 *
 * Two things this does NOT do, stated so the next reader does not have to derive
 * them from the code:
 *
 * - **It does not track an anchor that moves without scrolling or resizing.** A
 *   collapsible section expanding above the field, or a validation message
 *   appearing, moves the anchor without firing `scroll`, `resize` or a size
 *   change on the anchor itself, and the panel will not follow until something
 *   else nudges it. Closing that would mean an rAF loop for as long as a menu is
 *   open (what `floating-ui`'s `autoUpdate` does); it has not been worth the
 *   cost here, where menus are open for seconds and the layout above them is
 *   static while a field has focus.
 * - **It does not mirror for RTL.** It does not need to: the panel is
 *   width-matched to its anchor and aligned to the anchor's left edge, so
 *   left-alignment and right-alignment are the same placement.
 */

/** Distance between the anchor's edge and the panel. */
const GAP = 6;
/** Breathing room kept between the panel and the edge of the visible area. */
const EDGE = 8;
/**
 * The panel is never squeezed below this. Where the visible area really is
 * smaller, the panel overflows *downwards* rather than shrinking: the user can
 * scroll to it or dismiss the keyboard, but they cannot read a 20px list. It is
 * also why a panel only ever flips above when there is `MIN_HEIGHT` up there to
 * flip into — overflowing above the document origin is unreachable, and the
 * whole point of the exercise is that the list must not sit over the field.
 */
const MIN_HEIGHT = 96;
/** The panel's natural cap when there is plenty of room. */
const MAX_HEIGHT = 256;

export interface UseAnchoredPositionParams {
  /** The field the panel is placed against and width-matched to. */
  anchorRef: RefObject<HTMLElement | null>;
  /**
   * The positioned wrapper. Must already be `position: absolute` — this hook
   * writes `top`/`left`/`width`/`max-height` onto it and nothing else.
   */
  panelRef: RefObject<HTMLElement | null>;
  open: boolean;
}

/**
 * The invariant that keeps the panel's own ResizeObserver from feeding itself:
 * `maxHeight` and `width` are derived from the anchor and the viewport only,
 * never from the panel's measured size, so a panel resize can never produce a
 * different height to write. Do not make either depend on `panel.offsetHeight` —
 * a shrink-wrapping panel would oscillate. Skipping unchanged writes is a cheap
 * backstop, not the guarantee.
 */
function setStyle(el: HTMLElement, prop: 'top' | 'left' | 'width' | 'maxHeight', px: number) {
  const next = `${Math.round(px)}px`;
  if (el.style[prop] !== next) el.style[prop] = next;
}

function clamp(value: number, min: number, max: number) {
  // `max` can fall below `min` when the anchor is wider than the visible area;
  // the lower bound wins, so the panel stays reachable from the left edge.
  return Math.max(min, Math.min(max, value));
}

export function useAnchoredPosition({ anchorRef, panelRef, open }: UseAnchoredPositionParams) {
  const measure = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const rect = anchor.getBoundingClientRect();
    // `visualViewport` is absent in jsdom and on older browsers; the layout
    // viewport is the correct answer there, because those are exactly the
    // environments where the two cannot diverge.
    const vv = window.visualViewport;
    const viewTop = vv?.offsetTop ?? 0;
    const viewLeft = vv?.offsetLeft ?? 0;
    // `innerHeight`/`innerWidth` count classic scrollbars as visible space and
    // `clientHeight`/`clientWidth` do not, so prefer the latter — except where
    // nothing is laid out at all (jsdom reports 0), which no viewport ever is.
    const root = document.documentElement;
    const viewHeight = vv?.height ?? (root.clientHeight || window.innerHeight);
    const viewWidth = vv?.width ?? (root.clientWidth || window.innerWidth);

    const spaceBelow = viewHeight + viewTop - rect.bottom - GAP - EDGE;
    const spaceAbove = rect.top - viewTop - GAP - EDGE;
    // Below is the default. Flipping requires that above can seat a usable list,
    // not merely that it is the less bad of two bad options: a panel that
    // overflows downwards can be scrolled to, one that overflows above the
    // document origin cannot be reached at all. Nor does a shortfall alone flip
    // it — the menu would jump sides as rows stream in from the server.
    const above = spaceBelow < MIN_HEIGHT && spaceAbove >= MIN_HEIGHT && spaceAbove > spaceBelow;
    const room = above ? spaceAbove : spaceBelow;

    setStyle(panel, 'maxHeight', clamp(room, MIN_HEIGHT, MAX_HEIGHT));
    setStyle(panel, 'width', rect.width);
    setStyle(
      panel,
      'left',
      clamp(rect.left, viewLeft + EDGE, viewLeft + viewWidth - rect.width - EDGE) + window.scrollX,
    );

    // Read between two writes: an above-placed panel is positioned by its top
    // edge, so its laid-out height has to be known, and that is only settled
    // once `max-height` is on the element.
    const top = above ? rect.top - GAP - panel.offsetHeight : rect.bottom + GAP;
    setStyle(panel, 'top', top + window.scrollY);
  }, [anchorRef, panelRef]);

  useLayoutEffect(() => {
    if (!open) return;
    measure();

    const vv = window.visualViewport;
    // Capture phase: catches scrolling inside any ancestor container, not just
    // the window — which is most of them, in this app.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    // The keyboard. Nothing else reports it.
    vv?.addEventListener('resize', measure);
    vv?.addEventListener('scroll', measure);

    // Sizes change without anything scrolling: chips wrap onto a second line and
    // grow the anchor downwards, results stream in and grow an above-placed
    // panel upwards. Both used to leave the panel overlapping the field.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    if (anchorRef.current) observer?.observe(anchorRef.current);
    if (panelRef.current) observer?.observe(panelRef.current);

    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      vv?.removeEventListener('resize', measure);
      vv?.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, [open, measure, anchorRef, panelRef]);
}
